<template>
    <div class="pane-content">
        <!-- Start Warning Section -->
        <template v-if="!hasRequiredScopes && !isLoading">
            <el-row class="mx-2">
                <div class="p-3 mt-2" style="border: 1px solid grey; border-radius: 5px;">
                    <i class="el-icon-warning-outline mr-1"></i>
                    <span>
                        Please re-authenticate to acquire the missing HubSpot scopes for HubSpot Inbox and Conversations.
                    </span>
                </div>
            </el-row>
        </template>
        <!-- End Warning Section -->
        <template v-else>
            <!-- Start Channel Accounts Overview -->
            <el-row type="flex" class="mt-2">
                <h5 class="section-header">{{ integrationNameWithCasing }} Channel Accounts</h5>
            </el-row>
            <el-row type="flex" class="mt-2 mb-1">
                <span>
                    This section shows all {{ integrationNameWithCasing }} Channel Accounts that are connected to Aloware lines. Each channel account represents an Aloware line that has been configured as a custom channel in {{ integrationNameWithCasing }}. You can see information such as whether the channel is active, which inbox it's connected to, and the channel account details.
                </span>
            </el-row>
            <!-- End Channel Accounts Overview -->

            <!-- Start Statistics Overview -->
            <template v-if="isLoadingStats">
                <el-row type="flex" justify="start" class="mt-3 mb-1 stat-cards-container" :gutter="10">
                    <el-col :xs="24" :sm="12" :md="8" :lg="7" :xl="5" class="stat-card-col" v-for="i in 1" :key="i">
                        <div class="skeleton-card">
                            <el-skeleton animated>
                                <template slot="template">
                                    <div style="padding: 10px; position: relative; min-height: 100px;">
                                        <el-skeleton-item variant="text" style="width: 60%" />

                                        <!-- Loading Spinner -->
                                        <div class="skeleton-spinner-container">
                                            <i class="fa fa-spinner fa-spin"></i>
                                        </div>

                                        <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-top: 30px">
                                            <el-skeleton-item variant="h3" style="width: 50%" />
                                            <el-skeleton-item variant="text" style="width: 30%" />
                                        </div>
                                        <el-skeleton-item variant="text" style="width: 80%" />
                                        <div style="margin-top: 10px">
                                            <el-skeleton-item variant="button" style="width: 100%" />
                                        </div>
                                    </div>
                                </template>
                            </el-skeleton>
                        </div>
                    </el-col>
                </el-row>
            </template>
            <template v-else>
                <el-row type="flex" justify="start" class="mt-2 mb-1 stat-cards-container" :gutter="10">
                    <el-col :xs="24" :sm="12" :md="8" :lg="7" :xl="5" class="stat-card-col">
                        <settings-lines-stat-card
                            title="ALOWARE LINES"
                            :count="(statistics.connected_campaigns || 0) + '/' + (statistics.total_campaigns || 0)"
                            count-label="LINES CONNECTED"
                            action-button-text="VIEW LINES NOT CONNECTED"
                            action-button-icon="fa fa-eye"
                            :count-color="null"
                            :button-color="'#00BF50'"
                            :hide-action-button="statistics.unconnected_campaigns === 0"
                            @action-button-clicked="handleViewUnconnectedLines"
                        />
                    </el-col>
                    <el-col :xs="24" :sm="12" :md="8" :lg="7" :xl="5" class="stat-card-col">
                        <settings-lines-stat-card
                            title="PROBLEMATIC LINES"
                            :count="(statistics.problematic_count || 0) + '/' + (statistics.total_integrated || 0)"
                            count-label="LINES WITH ISSUES"
                            action-button-text=""
                            :count-color="hasProblematicLines ? '#F56C6C' : '#00BF50'"
                            :button-color="hasProblematicLines ? '#F56C6C' : '#00BF50'"
                            :hide-action-button="true"
                        />
                    </el-col>
                </el-row>
            </template>
            <!-- End Statistics Overview -->

            <!-- Start Sync Channel Accounts -->
            <el-row type="flex" class="mt-2 mb-2">
                <h5 class="section-header">Sync Channel Accounts</h5>
            </el-row>
            <el-row type="flex" :gutter="20" align="middle" class="mb-4">
                <el-col :span="15">
                    <div class="flex-column">
                        <span>
                            Use the <b>"Sync with HubSpot"</b> button to ensure that your {{ integrationNameWithCasing }} channel account configurations in Aloware match what's currently configured in {{ integrationNameWithCasing }}.
                        </span>
                        <br/>
                        <span>
                            Syncing happens automatically in real-time, but if you notice any discrepancies between what you see here and in {{ integrationNameWithCasing }}, you can click this button to manually sync. This will pull the latest channel account configurations from {{ integrationNameWithCasing }}.
                        </span>
                    </div>
                </el-col>
                <el-col :span="10">
                    <div class="flex-column justify-center h-100 ml-2">
                        <div>
                            <el-button
                                @click="syncInboxes"
                                :disabled="isSyncingInboxes || !hasRequiredScopes"
                                :loading="isSyncingInboxes"
                                size="mini"
                                icon="el-icon-refresh"
                            >
                                Sync with HubSpot
                            </el-button>
                            <small v-if="model && model.settings && model.settings.last_sync_inbox_configurations" class="mt-2 ml-2 sync-time-text">
                                Last channel account sync: <b>{{ model.settings.last_sync_inbox_configurations | fixFullDateUTCRelative }}</b>
                            </small>
                        </div>
                    </div>
                </el-col>
            </el-row>
            <!-- End Sync Channel Accounts -->

            <!-- Start Note Banner -->
            <div class="p-3 container mb-3 mt-2">
                <i class="el-icon-warning-outline mr-1"></i>
                <span>
                    An Aloware line can only be connected to one {{ integrationNameWithCasing }} Inbox at a time. If you need to connect to a different inbox, you can either disconnect the current line or use a different Aloware line.
                </span>
            </div>
            <!-- End Note Banner -->

            <!-- Start Loading State -->
            <div v-if="isLoading" class="text-center py-4">
                <i class="el-icon-loading"></i>
                                        <span class="ml-2">Loading channel accounts...</span>
            </div>
            <!-- End Loading State -->

            <!-- Start Empty State -->
            <div v-else-if="channels.length === 0" class="empty-state">
                <i class="el-icon-message"></i>
                <p>No {{ integrationNameWithCasing }} Channel Accounts are currently connected.</p>
                <p class="text-small">Connect your first Aloware line as a custom channel to get started.</p>
            </div>
            <!-- End Empty State -->

            <!-- Start Channels List -->
            <div v-else class="channels-list">
                <el-row type="flex" justify="start" class="mt-3 mb-1 stat-cards-container" :gutter="10">
                    <el-col :xs="24" :sm="12" :md="8" :lg="8" :xl="8" class="stat-card-col" v-for="channel in channels" :key="`${channel.channel_id}-${channel.channel_account_id}`">
                        <div
                            class="stat-card"
                            :class="{
                                'stat-card-error': channel.error,
                                'stat-card-inactive': !channel.error && !channel.is_active
                            }"
                        >
                            <div class="stat-card-title">
                                <span>{{ channel.channel_name }}</span>
                            </div>
                            <div class="stat-card-info">
                                <div class="info-line">
                                    {{ channel.inbox_name }}
                                    <div class="info-label">INBOX NAME</div>
                                </div>
                                <div class="info-line info-line-spaced">
                                    {{ channel.channel_account_name }}
                                    <div class="info-label">CHANNEL ACCOUNT</div>
                                </div>
                            </div>
                            <div class="stat-card-footer">
                                <div class="link-icon">
                                    <a
                                        v-if="channel.link"
                                        :href="channel.link"
                                        target="_blank"
                                        title="Open HubSpot Channel Account Settings"
                                        :style="{
                                            color: channel.error ? '#e74c3c' : (!channel.is_active ? '#ff9800' : '#00BF50')
                                        }"
                                    >
                                        <i class="fa fa-external-link-alt"></i>
                                    </a>
                                </div>
                                <div class="status-tag" style="display: flex; align-items: center;">
                                    <template v-if="channel.error">
                                        <el-tooltip :content="channel.error" placement="top" effect="dark">
                                            <el-tag style="background-color: #e74c3c; color: #fff; border: none;" size="small">Invalid</el-tag>
                                        </el-tooltip>
                                    </template>
                                    <template v-if="channel.is_active && !channel.error">
                                        <el-tag type="success" size="small">Active</el-tag>
                                    </template>
                                    <template v-else-if="!channel.is_active && !channel.error">
                                        <el-tooltip content="This channel account is deactivated in HubSpot" placement="top" effect="dark">
                                            <el-tag style="background-color: #ff9800; color: #fff; border: none;" size="small">Inactive</el-tag>
                                        </el-tooltip>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </el-col>
                </el-row>
            </div>
            <!-- End Channels List -->
        </template>

        <!-- Start Unconnected Lines Dialog -->
        <el-dialog
            :visible.sync="unconnectedLinesDialogVisible"
            width="80%"
            :before-close="handleCloseUnconnectedDialog"
            :append-to-body="true"
            custom-class="dialog-higher-position"
            title="Lines Not Connected"
        >
            <!-- Start Search Bar -->
            <el-input
                v-model="unconnectedLinesSearchQuery"
                placeholder="Search by Name or ID"
                clearable
                @clear="handleUnconnectedLinesSearch"
                @keyup.enter.native="handleUnconnectedLinesSearch"
                class="mb-3"
            >
                <el-button
                    slot="append"
                    icon="el-icon-search"
                    @click="handleUnconnectedLinesSearch"
                    :loading="isLoadingUnconnectedLines"
                    :disabled="isLoadingUnconnectedLines"
                ></el-button>
            </el-input>
            <!-- End Search Bar -->

            <!-- Start Loading Indicator -->
            <template v-if="isLoadingUnconnectedLines">
                <el-row class="mt-4 mb-4">
                    <el-col :span="24" class="text-center">
                        <div class="empty-state">
                            <i class="fa fa-spinner fa-spin"></i>
                            <p>Loading Lines Not Connected</p>
                        </div>
                    </el-col>
                </el-row>
            </template>
            <!-- End Loading Indicator -->

            <!-- Start Lines Table -->
            <template v-else>
                <el-table
                    :data="unconnectedLines"
                    style="width: 100%"
                    @row-click="openLineTab"
                    :row-class-name="rowClassName"
                    :hover="true"
                    class="unconnectedLinesTable">
                    <template slot="empty">
                        <!-- Custom empty slot - intentionally left blank -->
                    </template>
                    <el-table-column label="ID" prop="id" width="80"></el-table-column>
                    <el-table-column label="Name" prop="name"></el-table-column>
                    <el-table-column label="Phone Numbers" prop="phone_numbers">
                        <template slot-scope="scope">
                            <span v-if="scope.row.phone_numbers.length <= 5">
                                <span v-for="(phone, index) in scope.row.phone_numbers" :key="index">
                                    {{ phone }}{{ index < scope.row.phone_numbers.length - 1 ? ', ' : '' }}
                                </span>
                            </span>
                            <span v-else>
                                <span v-for="(phone, index) in scope.row.phone_numbers.slice(0, 5)" :key="index">
                                    {{ phone }}{{ index < 4 ? ', ' : '' }}
                                </span>
                                <span class="more-numbers">+ {{ scope.row.phone_numbers.length - 5 }} more</span>
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column label="SMS & MMS Capable" prop="capabilities">
                        <template slot-scope="scope">
                            <div class="capabilities-container">
                                <span v-if="scope.row.capabilities.sms || scope.row.capabilities.mms" class="capable-yes">
                                    <i class="el-icon-check"></i>
                                    Yes
                                </span>
                                <span v-else class="capable-no">
                                    <i class="el-icon-close"></i>
                                    No
                                </span>
                            </div>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- Start Pagination -->
                <el-row v-if="unconnectedLines.length > 0" class="pagination-container mt-4 mb-3">
                    <el-col :span="24" class="text-center">
                        <el-pagination
                            @current-change="handleUnconnectedLinesPageChange"
                            :current-page.sync="unconnectedLinesPage"
                            :page-size="unconnectedLinesPageSize"
                            layout="prev, pager, next"
                            :total="unconnectedLinesTotalItems">
                        </el-pagination>
                    </el-col>
                </el-row>
                <!-- End Pagination -->
            </template>
            <!-- End Lines Table -->
        </el-dialog>
        <!-- End Unconnected Lines Dialog -->
    </div>
</template>

<script>
import SettingsLinesStatCard from '../settings-lines-stat-card.vue'

export default {
    name: 'SettingsLinesPane',
    components: {
        SettingsLinesStatCard
    },
    props: {
        model: {
            type: Object,
            required: true
        },
        integrationNameWithCasing: {
            type: String,
            required: true
        },
        staticName: {
            type: String,
            required: false,
            default: 'Aloware'
        }
    },
    data() {
        return {
            channels: [],
            isLoading: false,
            isSyncingInboxes: false,
            hasRequiredScopes: true,
            isLoadingStats: false,
            statistics: {
                unconnected_campaigns: 0,
                connected_campaigns: 0,
                total_campaigns: 0,
                problematic_count: 0,
                total_integrated: 0
            },
            unconnectedLinesDialogVisible: false,
            unconnectedLinesSearchQuery: '',
            unconnectedLines: [],
            isLoadingUnconnectedLines: false,
            unconnectedLinesPage: 1,
            unconnectedLinesPageSize: 15,
            unconnectedLinesTotalItems: 0,
            isFullSync: false
        }
    },
    computed: {
        baseUrl() {
            return window.location.origin
        },
        hasProblematicLines() {
            return (this.statistics.problematic_count || 0) > 0
        }
    },
    async created() {
        await this.loadConnectedChannels()
        await this.fetchStatisticsData()
    },
    methods: {
        async fetchStatisticsData(fresh = false) {
            this.isLoadingStats = true

            try {
                const response = await axios.get('/api/v1/integrations/hubspot/inbox/statistics')
                if (response.data.success) {
                    const stats = response.data.data
                    this.statistics.unconnected_campaigns = stats.unconnected_campaigns || 0
                    this.statistics.connected_campaigns = stats.connected_campaigns || 0
                    this.statistics.total_campaigns = stats.total_campaigns || 0
                    this.statistics.problematic_count = stats.problematic_count || 0
                    this.statistics.total_integrated = stats.total_integrated || 0
                    this.isFullSync = stats.unconnected_campaigns === 0
                }
            } catch (error) {
                this.$root.handleErrors(error.response)
            } finally {
                this.isLoadingStats = false
            }
        },

        handleViewUnconnectedLines() {
            this.unconnectedLinesDialogVisible = true
            this.fetchUnconnectedLines()
        },

        async fetchUnconnectedLines() {
            try {
                this.isLoadingUnconnectedLines = true

                const response = await axios.get('/api/v1/integrations/hubspot/inbox/unconnected-campaigns', {
                    params: {
                        page: this.unconnectedLinesPage,
                        per_page: this.unconnectedLinesPageSize,
                        query: this.unconnectedLinesSearchQuery
                    }
                })

                if (response.data.success) {
                    this.unconnectedLines = response.data.data || []

                    if (response.data.pagination) {
                        const pagination = response.data.pagination
                        this.unconnectedLinesTotalItems = pagination.total || 0
                    } else {
                        this.unconnectedLinesTotalItems = this.unconnectedLines.length
                    }
                }
            } catch (error) {
                console.error('Error fetching lines not connected:', error)
                this.$notify({
                    title: 'API Error',
                    message: 'Failed to load lines not connected. Please check the API endpoint.',
                    type: 'error'
                })

                this.unconnectedLines = []
                this.unconnectedLinesTotalItems = 0
            } finally {
                this.isLoadingUnconnectedLines = false
            }
        },

        handleUnconnectedLinesPageChange(newPage) {
            this.unconnectedLinesPage = newPage
            this.fetchUnconnectedLines()
        },

        handleUnconnectedLinesSearch() {
            this.unconnectedLinesPage = 1
            this.fetchUnconnectedLines()
        },

        handleCloseUnconnectedDialog() {
            this.unconnectedLinesDialogVisible = false
            this.unconnectedLinesPage = 1
            this.unconnectedLinesSearchQuery = ''
        },

        openLineTab(row) {
            // Open line settings in a new tab with the correct URL format
            const baseUrl = window.location.origin
            window.open(`${baseUrl}/lines/${row.id}/settings`, '_blank')
        },

        rowClassName() {
            return 'row-hover-green el-table-row-custom'
        },

        async refreshStatistics() {
            if (this.isLoadingStats) return
            await this.fetchStatisticsData(true)
        },

        async loadConnectedChannels() {
            this.isLoading = true

            try {
                const response = await axios.get('/api/v1/integrations/hubspot/inbox/connected-channel-accounts')
                if (response.data.success) {
                    this.channels = response.data.data.channels
                    this.hasRequiredScopes = response.data.data.has_required_scopes !== false
                } else {
                    this.hasRequiredScopes = true
                    this.$root.handleErrors(response)
                }
            } catch (error) {
                this.hasRequiredScopes = true
                this.$root.handleErrors(error.response)
            } finally {
                this.isLoading = false
            }
        },
        async syncInboxes() {
            this.isSyncingInboxes = true

            try {
                const response = await axios.post('/api/v1/integrations/hubspot/inbox/sync-configurations')

                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: `HubSpot channel account configurations are being synced, please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.`,
                    type: 'success'
                })

                // Reload the channels to show the updated state
                await this.loadConnectedChannels()

                // Refresh statistics after sync
                await this.fetchStatisticsData()
            } catch (error) {
                this.$root.handleErrors(error.response)
            } finally {
                this.isSyncingInboxes = false
            }
        },

    }
}
</script>

<style scoped>
.pane-content {
    display: flex;
    flex-direction: column;
    padding-left: 8px;
    padding-right: 8px;
}

.section-header {
    margin: 0 12px 0 0;
    font-size: 20px;
}

.stat-cards-container {
    flex-wrap: wrap;
    margin-bottom: 20px;
}

.stat-card-col {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 20px;
}

.stat-card {
    min-width: 150px;
    max-width: 380px;
    width: 100%;
    border-radius: 10px;
    border: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    padding: 10px 15px;
    margin: 0;
    min-height: 160px;
}

.stat-card-title {
    margin-bottom: 4px;
}

.stat-card-title span {
    font-size: 16px;
    font-weight: bold;
    color: #303133;
}

.stat-card-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 24px;
    padding-top: 0;
}

.info-line {
    font-size: 15px;
    font-weight: 500;
    color: #606266;
    margin-bottom: 2px;
    text-align: center;
}

.info-line small {
    color: #606266;
    font-size: 13px;
}

.stat-card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 10px;
    padding-top: 10px;
}

.link-icon a {
    color: #00BF50;
    font-size: 16px;
    text-decoration: none;
}

.link-icon a:hover {
    color: #13d672;
}

.status-tag {
    margin-left: auto;
}

.status-tag .el-tag {
    border: none;
    border-radius: 4px;
    font-weight: 500;
    color: white;
}

.status-tag .el-tag--success {
    background-color: #00BF50;
    color: #fff;
}

.status-tag .el-tag--danger {
    background-color: #C0C4CC;
    color: #fff;
}

.container {
    border: 1px solid grey;
    border-radius: 5px;
    width: 100%;
    max-width: 100%;
}

.empty-state {
    padding: 30px 0;
    text-align: center;
    color: #909399;
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 15px;
}

.empty-state p {
    font-size: 16px;
    margin: 0 0 5px;
}

.empty-state .text-small {
    font-size: 14px;
    color: #C0C4CC;
}

.alert {
    padding: 12px 16px;
    border-radius: 4px;
    margin-bottom: 20px;
}

.alert-danger {
    background-color: #fef0f0;
    border: 1px solid #fbc4c4;
    color: #a94442;
}





.text-center {
    text-align: center;
}

.py-4 {
    padding-top: 20px;
    padding-bottom: 20px;
}

/* Flex utilities */
.flex-column {
    display: flex;
    flex-direction: column;
}

.justify-center {
    justify-content: center;
}

.h-100 {
    height: 100%;
}

.sync-time-text {
    display: block;
    color: #909399;
    font-size: 12px;
    margin-top: 8px;
}

@media (max-width: 768px) {
    .stat-card-col {
        padding-left: 0;
    }
}

.stat-card-error {
    border: 2px solid #e74c3c !important;
    box-shadow: 0 0 0 2px #fbeaea;
}
.stat-card-inactive {
    border: 2px solid #ff9800 !important;
    box-shadow: 0 0 0 2px #fff3e0;
}

/* Statistics section styles */
.stat-cards-container {
    flex-wrap: wrap;
    margin-bottom: 20px;
}

.stat-card-col {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 20px;
}

@media (max-width: 768px) {
    .stat-card-col {
        padding-left: 0;
    }
}

/* Skeleton styling */
.skeleton-card {
    background-color: #f5f7fa;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    width: 100%;
    min-width: 150px;
    max-width: 380px;
    min-height: 120px;
    position: relative;
}

.skeleton-spinner-container {
    position: absolute;
    top: 20px;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
}

.skeleton-spinner-container i {
    font-size: 16px;
    color: #909399;
}

/* Dialog positioning */
::v-deep .dialog-higher-position {
    margin-top: 5vh !important;
}

.refresh-link {
    color: #00BF50;
    text-decoration: none;
    font-weight: 500;
}

.refresh-link:hover {
    text-decoration: underline;
}

.refresh-link-loading {
    color: #85e0ad;
    cursor: wait;
    pointer-events: none;
}

.refresh-link-loading:hover {
    text-decoration: none;
}

/* Custom row hover style for tables */
.row-hover-green:hover {
    cursor: pointer !important;
    background-color: rgba(0, 191, 80, 0.1) !important;
    text-decoration: underline !important;
}

.row-hover-green:hover td {
    color: #00BF50 !important;
}

/* Target only unconnected lines table rows */
::v-deep .unconnectedLinesTable tbody tr:hover {
    cursor: pointer !important;
}

::v-deep .unconnectedLinesTable tbody tr:hover td {
    background-color: rgba(0, 191, 80, 0.1) !important;
    color: #00BF50 !important;
    text-decoration: underline !important;
}

::v-deep .el-table__row.el-table-row-custom:hover > td {
    background-color: rgba(0, 191, 80, 0.1) !important;
}

.pagination-container {
    margin-top: 20px;
}

/* Style the pagination buttons */
::v-deep .el-pagination .el-pager li.active {
    color: #00BF50;
    font-weight: bold;
}

::v-deep .el-pagination .el-pager li:hover {
    color: #00BF50;
}

::v-deep .el-pagination button:hover {
    color: #00BF50;
}

/* Capabilities column styling */
.capabilities-container {
    display: flex;
    align-items: center;
}

.capable-yes {
    display: flex;
    align-items: center;
    gap: 4px;
}

.capable-yes i {
    font-size: 14px;
}

.capable-no {
    display: flex;
    align-items: center;
    gap: 4px;
}

.capable-no i {
    font-size: 14px;
}

.more-numbers {
    color: #909399;
    font-size: 12px;
    font-style: italic;
    margin-left: 4px;
}

.info-label {
    font-size: 10px;
    font-weight: 500;
    color: #909399;
    text-transform: uppercase;
    margin-top: 2px;
}

.info-line-spaced {
    margin-top: 12px;
}
</style>
